import { Page } from '@app/components/page/Page';
import { Paginated } from '@mi-front/core';
import { useQuery } from '@tanstack/react-query';
import { MRT_PaginationState, MRT_SortingState } from 'material-react-table';
import { ComponentType, useCallback } from 'react';
import { ListViewContext } from './context/ListViewContext';
import { DataToolbar } from './DataToolbar';
import { useListViewLogic, UseListViewLogicProps } from './hooks/useListViewLogic';
import { ListEmptyState } from './ListEmptyState';
import { ListViewFilters, ListViewQueryParams } from './types';

export type PaginationChangeHandler = (
  updaterOrValue: MRT_PaginationState | ((old: MRT_PaginationState) => MRT_PaginationState),
) => void;

export type SortingChangeHandler = (
  updaterOrValue: MRT_SortingState | ((old: MRT_SortingState) => MRT_SortingState),
) => void;

export interface ListViewChildProps<TData> {
  data: TData[];
  total: number;
  pagination: MRT_PaginationState;
  onPaginationChange: PaginationChangeHandler;
  sorting: MRT_SortingState;
  onSortingChange: SortingChangeHandler;
  isLoading?: boolean;
  isFetching?: boolean;
  showProgress?: boolean;
}

export interface ListViewPagedProps<
  TData,
  _TFilters extends ListViewFilters,
> extends UseListViewLogicProps {
  queryFn: (params: ListViewQueryParams) => Promise<Paginated<TData> | ArrayBuffer>;
  ListComponent: ComponentType<ListViewChildProps<TData>>;
  actions?: React.ReactNode[];
  showProgress?: boolean;
  ListWrapper?: ComponentType<{ children: React.ReactNode }>;
}

/**
 * Main ListView component that encapsulates URL state management, React Query data fetching,
 * pagination handling, and filter management.
 */
export function ListViewPaged<TData, _TFilters extends ListViewFilters>({
  filters,
  listId,
  queryFn,
  exportFn,
  ListComponent,
  actions = [],
  defaultPageSize = 50,
  stickyFilters = true,
  showProgress = true,
  ListWrapper = Page.Content,
}: ListViewPagedProps<TData, _TFilters>) {
  const {
    allParams,
    setAllParams,
    contextValue,
    onExport,
    onRefresh,
    handleSortingChange,
    sorting,
  } = useListViewLogic({
    listId,
    filters,
    defaultPageSize,
    stickyFilters,
    exportFn,
  });

  const { pageIndex, pageSize, sort, ...urlFilters } = allParams;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [listId, allParams],
    queryFn: async () => {
      const result = await queryFn({
        pageIndex,
        pageSize,
        sort: sort,
        filters: urlFilters,
      });
      return result as Paginated<TData>;
    },
    placeholderData: (previousData) => previousData,
  });

  const handlePaginationChange: PaginationChangeHandler = useCallback(
    (updaterOrValue) => {
      const newPagination =
        typeof updaterOrValue === 'function'
          ? updaterOrValue({ pageIndex, pageSize })
          : updaterOrValue;
      setAllParams((old) => ({
        ...old,
        pageIndex: newPagination.pageIndex,
        pageSize: newPagination.pageSize,
      }));
    },
    [pageIndex, pageSize, setAllParams],
  );

  const total = data?.metadata.totalCount ?? 0;
  const hasNoResults = !isLoading && total === 0;

  return (
    <ListViewContext.Provider value={contextValue}>
      <DataToolbar
        actions={actions}
        onExport={onExport}
        onRefresh={onRefresh}
        total={total}
        isLoading={isFetching}
        showProgress={showProgress}
      />
      <ListWrapper>
        {hasNoResults ? (
          <ListEmptyState />
        ) : (
          <ListComponent
            data={data?.results || []}
            total={total}
            isLoading={isLoading}
            isFetching={isFetching}
            pagination={{ pageIndex, pageSize }}
            onPaginationChange={handlePaginationChange}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            showProgress={false}
          />
        )}
      </ListWrapper>
    </ListViewContext.Provider>
  );
}
